import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypePrism from 'rehype-prism-plus';
import { setChatsList, setCurrentChatId } from '../store/chatSlice';
import { getChatHistory, getChatName, newChat, sendMessageToChatStream } from '../api/apiApi';
export function getInputElement() {
    return document.getElementById('text');
}
export const ChatComponent = () => {
    const dispatch = useDispatch();
    const currentChatId = useSelector((state) => state.chat.currentChatId);
    const chatsList = useSelector((state) => state.chat.chatsList);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    useEffect(() => {
        loadChatHistory();
    }, [currentChatId]);
    // Auto-scroll to bottom when chat is opened and messages are loaded
    useLayoutEffect(() => {
        if (messages.length > 0) {
            scrollToBottom();
        }
    }, [currentChatId, messages.length]);
    async function loadChatHistory() {
        setMessages([{ id: 'loading', role: 'system', content: 'Loading history...' }]);
        const chatHistory = await getChatHistory();
        if (!chatHistory.result || !chatHistory.data) {
            setMessages([
                { id: 'err', role: 'system', content: `[Error ${chatHistory.errorMessage}] Could not load history` },
            ]);
        }
        else {
            const msgs = (chatHistory.data.messages ?? []).map((m, idx) => ({
                id: `h-${idx}`,
                role: m.role ?? 'assistant',
                content: m.content ?? '',
            }));
            setMessages(msgs);
        }
        getInputElement()?.focus();
    }
    function scrollToBottom() {
        const element = document.getElementById('msgs');
        if (!element)
            return;
        element.scrollTop = element.scrollHeight;
    }
    function onEnterMessage(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    }
    async function send() {
        if (!input.trim())
            return;
        let chatIdToUse = currentChatId;
        // chat
        let isNewChat = false;
        let newChatMetadata = null;
        if (!chatIdToUse) {
            isNewChat = true;
            newChatMetadata = await newChat();
            if (!newChatMetadata.result || !newChatMetadata.data) {
                return;
            }
            chatIdToUse = newChatMetadata.data.chatId;
        }
        if (!chatIdToUse)
            return;
        // message
        const userMessage = input;
        setInput('');
        const userMsg = { id: `u-${Date.now()}`, role: 'user', content: userMessage };
        setMessages((prev) => [...prev, userMsg]);
        const assistantId = `a-${Date.now()}`;
        setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '', isStreaming: true }]);
        scrollToBottom();
        await sendMessageToChatStream(chatIdToUse, userMessage, {
            onChunk: (chunk) => {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: (m.content ?? '') + chunk } : m)));
                scrollToBottom();
            },
            onDone: () => {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)));
            },
            onError: (err) => {
                setMessages((prev) => [...prev, { id: `sys-${Date.now()}`, role: 'system', content: `Error: ${err}` }]);
            },
        });
        // chat
        if (isNewChat) {
            if (!newChatMetadata || !newChatMetadata.result || !newChatMetadata.data)
                return;
            newChatMetadata.data.chatName = await getChatName(newChatMetadata.data.chatId);
            const updatedChatsList = [...chatsList, newChatMetadata.data];
            updatedChatsList.sort((a, b) => (a.lastActiveAt < b.lastActiveAt ? 1 : -1));
            dispatch(setChatsList(updatedChatsList));
            dispatch(setCurrentChatId(chatIdToUse));
        }
    }
    return (_jsxs("div", { className: "chat-root", children: [_jsx("div", { id: "msgs", className: "msgs", children: messages.map((m) => {
                    const badge = m.role === 'assistant' ? 'Assistant' : m.role === 'user' ? 'You' : 'System';
                    const showLoadingDots = m.isStreaming && (!m.content || m.content.trim() === '');
                    const mdComponents = {
                        a: ({ node, ...props }) => _jsx("a", { href: `/external?url=${props.href}`, target: "_blank", rel: "noopener noreferrer", children: props.children })
                    };
                    const markdownDuringStream = (_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], rehypePlugins: [rehypePrism], components: mdComponents, children: m.content }, m.id + ':' + m.content.length));
                    const markdownFinal = (_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], rehypePlugins: [rehypePrism], components: mdComponents, children: m.content }));
                    return (_jsxs("div", { className: `msg ${m.role}`, children: [_jsx("div", { className: "msg-header", children: _jsx("span", { className: "badge", children: badge }) }), _jsx("div", { className: "msg-body", children: showLoadingDots ? (_jsxs("div", { className: "loadingDots", children: [_jsx("i", {}), _jsx("i", {}), _jsx("i", {})] })) : m.isStreaming ? (markdownDuringStream) : (markdownFinal) })] }, m.id));
                }) }), _jsxs("div", { className: "composer", children: [_jsx("textarea", { id: "text", value: input, onChange: (e) => setInput(e.target.value), onKeyDown: onEnterMessage, placeholder: "Enter a message...", rows: 3 }), _jsx("button", { onClick: send, title: "Send", children: _jsx("svg", { viewBox: "0 0 100 100", children: _jsx("use", { xlinkHref: "#sendThumb" }) }) })] })] }));
};
